import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Screen } from "@/components";
import { useUserState } from "@moonscape/shared";
import { trackEvent } from "@/analytics";
import { SUBSCRIPTION_EVENTS } from "@/analytics/events";
import { Callout, Lab, Padlock, Pill } from "@/components/ms";
import { usePreFigureState } from "./preFigure";

interface TaxPremiumGateProps {
  children: React.ReactNode;
  trigger?: string;
}

const GateShell = ({
  eyebrow,
  heading,
  children,
  action,
}: {
  eyebrow: string;
  heading: string;
  children: React.ReactNode;
  action: React.ReactNode;
}) => (
  <Screen heading="Tax" className="">
    <div className="mx-auto flex min-h-[60vh] max-w-[46ch] flex-col items-center justify-center text-center">
      <span className="inline-flex text-ms-dim">
        <Padlock size={22} />
      </span>
      <Lab className="mt-4 block">{eyebrow}</Lab>
      <h2 className="mt-2 text-[clamp(22px,3vw,28px)] font-semibold tracking-[-0.03em] text-ms-ink">
        {heading}
      </h2>
      <div className="mt-3 text-[13.5px] leading-[1.5] text-ms-mute">{children}</div>
      <div className="mt-6">{action}</div>
      <Link
        to="/tax"
        className="mt-4 text-[12px] text-ms-mute transition-colors hover:text-ms-ink"
      >
        Back to tax overview
      </Link>
    </div>
  </Screen>
);

/**
 * Wraps a tax sub-screen. Shows full-page paywall for free users
 * AND for premium users who have exceeded their plan's transaction limit.
 */
export const TaxPremiumGate = ({
  children,
  trigger = "tax_sub_screen",
}: TaxPremiumGateProps) => {
  const { data: userState, isLoading } = useUserState();
  const isPremium = userState?.isPremium ?? false;
  const metrics = userState?.metrics;
  const isOverPlanLimit = metrics?.isOverPlanLimit ?? false;
  const isGrandfathered = metrics?.isGrandfathered ?? false;
  const isBlocked = !isPremium || (isPremium && isOverPlanLimit && !isGrandfathered);

  // No figure yet -> no price. The screen is still gated, but it shows the job
  // the user is waiting on rather than a price for a number they cannot see
  // (see ./preFigure). /plans stays reachable directly for a willing buyer.
  const preFigure = usePreFigureState();
  const priced = !preFigure;

  useEffect(() => {
    if (!isLoading && isBlocked && priced) {
      trackEvent(SUBSCRIPTION_EVENTS.PAYWALL_SHOWN, {
        trigger,
        source: "web",
        screen: "tax",
        reason: !isPremium ? "free_user" : "txn_limit_exceeded",
        had_completed_calc: true,
      });
    }
  }, [isLoading, isBlocked, isPremium, trigger, priced]);

  if (isLoading) return null;

  if (isBlocked && preFigure) {
    return (
      <GateShell
        eyebrow={preFigure.eyebrow}
        heading={preFigure.heading}
        action={
          <Pill as={Link} to={preFigure.ctaTo}>
            {preFigure.ctaLabel}
          </Pill>
        }
      >
        {preFigure.body}
      </GateShell>
    );
  }

  if (!isPremium) {
    return (
      <GateShell
        eyebrow="Locked"
        heading="Premium feature"
        action={
          <Pill as={Link} to="/plans">
            Upgrade plan
          </Pill>
        }
      >
        Upgrade your plan to access detailed tax reports, forms, and audit tools.
      </GateShell>
    );
  }

  if (isOverPlanLimit && !isGrandfathered) {
    const txCount = metrics?.txCount ?? 0;
    const planTxnLimit = metrics?.planTxnLimit;
    const nextPlanName = metrics?.nextPlanName;
    const nextPlanSlug = metrics?.nextPlanSlug;

    return (
      <GateShell
        eyebrow="Over your cap"
        heading="You've outgrown your plan"
        action={
          <Pill
            as={Link}
            to="/plans"
            onClick={() => {
              trackEvent(SUBSCRIPTION_EVENTS.PAYWALL_SHOWN, {
                trigger: "txn_limit_form_gate",
                current_plan: metrics?.planSlug,
                next_plan: nextPlanSlug,
                txn_count: txCount,
                source: "web",
                had_completed_calc: true,
              });
            }}
          >
            Upgrade to {nextPlanName || "a higher plan"}
          </Pill>
        }
      >
        <p>
          You have{" "}
          <span className="font-mono tabular-nums text-ms-ink">
            {txCount.toLocaleString()}
          </span>{" "}
          transactions
          {planTxnLimit ? (
            <>
              {" "}
              against a cap of{" "}
              <span className="font-mono tabular-nums text-ms-ink">
                {planTxnLimit.toLocaleString()}
              </span>
            </>
          ) : null}
          .
        </p>
        <Callout tone="tan" className="mt-4 text-left">
          {nextPlanName
            ? `Upgrade to ${nextPlanName} to unlock your tax reports.`
            : "Upgrade your plan to unlock your tax reports."}{" "}
          Your figures keep calculating either way — only the export is gated.
        </Callout>
      </GateShell>
    );
  }

  return <>{children}</>;
};
